import React from 'react'

import { RHFInput } from 'react-hook-form-input'
import useForm from 'react-hook-form'

import {
  Button,
  Divider,
  TextField,
  Tooltip,
  IconButton,
  Typography,
  makeStyles,
  Box,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core'

import { Delete as DeleteIcon } from '@material-ui/icons'

import { InputAutocomplete, InputCurrency } from 'components/Form'
import useCollapse from 'hooks/useCollapse'
import * as service from 'service'
import helpers from 'helpers'
import schemas from './../../../../../schemas'
import styles from './styles'
import useRecurringCheck from 'hooks/useRecurringCheck'

const useStyles = makeStyles(styles)

const CollapseBody = ({ item }) => {
  const classes = useStyles()
  const collapse = useCollapse()
  const {
    setQuantityChecked,
    handleChangeCheck,
    handleChangeQuantity,
    checkRecurrentValue,
  } = useRecurringCheck()
  const inputQuantityRef = React.useRef()

  const { register, setValue, errors, handleSubmit, watch } = useForm({
    validationSchema: schemas.contractStepPlan,
    defaultValues: {
      ...item,
      quantity: item.quantity.toString(),
      'contractPlanEntries[0].quantity': item.contractPlanEntries[0].quantity,
      'contractPlanEntries[0].price': item.contractPlanEntries[0].price,
    },
  })

  const values = watch()

  const onSubmit = async contractPlan => {
    const data = {
      ...contractPlan,
      collapseSettings: { expand: false, edit: false },
    }

    collapse.update(item.collapseId, data)
  }

  const handleClickEditButton = event => {
    event.preventDefault()

    collapse.toggleEdit(item.collapseId)
  }

  const handleClickCancelButton = event => {
    event.preventDefault()

    const prevItem = collapse.get(item.collapseId)
    const hasContractPlanEntries =
      prevItem.contractPlanEntries && prevItem.contractPlanEntries.length

    setValue('plan', prevItem.plan, true)
    setValue('periodicity', prevItem.periodicity, true)
    setValue('quantity', String(prevItem.quantity), true)
    setValue('price', prevItem.price, true)
    setValue(
      'contractPlanEntries[0].quantity',
      hasContractPlanEntries ? prevItem.contractPlanEntries[0].quantity : '',
      true,
    )
    setValue(
      'contractPlanEntries[0].price',
      hasContractPlanEntries ? prevItem.contractPlanEntries[0].price : '',
      true,
    )
    setQuantityChecked({
      quantity: helpers.contractPlans.isRecurring(prevItem.quantity),
      'contractPlanEntries[0].quantity': helpers.contractPlanEntries.isRecurring(
        prevItem.contractPlanEntries[0].quantity,
      ),
    })

    collapse.toggleEdit(item.collapseId, true)
  }

  const handleChangePlan = event => {
    const plan = event.target.value

    if (!plan) {
      setValue('periodicity', '')
      setValue('quantity', '')
      setValue('contractPlanEntries[0].quantity', '')
      setValue('contractPlanEntries[0].price', 0)
      setValue('price', 0)
      setQuantityChecked({})
      return
    }

    setValue('periodicity', plan.periodicity, true)
    setValue('price', plan.price, true)
    setImmediate(() => inputQuantityRef.current.focus())
  }

  const handleClickRemoveButton = () => {
    collapse.remove(item.collapseId)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={classes.root}>
      <div className={classes.container}>
        <div className={classes.rowInput}>
          <RHFInput
            as={
              <InputAutocomplete
                fn={service.recurrence.plans.get}
                fnLabel={option => option.name || ''}
                entity="plans"
                keyEntity="id"
                searchParam="plan[name]"
                fixedFilter={{ perPage: 10 }}
                textFieldProps={{
                  label: 'Planos',
                  margin: 'dense',
                  variant: 'outlined',
                  fullWidth: true,
                  error: !!errors.plan,
                  helperText: errors && errors.plan && errors.plan.message,
                }}
                className={`${classes.input} ${classes.inputHalf}`}
                size="small"
                disabled={!item.collapseSettings.edit}
              />
            }
            name="plan"
            register={register}
            setValue={setValue}
            onChange={handleChangePlan}
            mode="onChange"
          />
        </div>

        <Box mt={2}>
          <Typography color="textSecondary">
            Periodicidade da cobrança (de quanto em quanto tempo será cobrado?)
          </Typography>
        </Box>
        <RHFInput
          as={
            <TextField
              label="Periodicidade"
              className={`${classes.input} ${classes.inputHalf}`}
              SelectProps={{ native: true }}
              size="small"
              margin="dense"
              select
              error={errors.periodicity !== undefined}
              helperText={errors.periodicity?.message}
              disabled={!item.collapseSettings.edit}
              fullWidth
            >
              <option></option>
              {helpers.plans.periodicityOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TextField>
          }
          name="periodicity"
          register={register}
          setValue={setValue}
          mode="onChange"
        />

        <div className={classes.rowInput}>
          <Box className={`${classes.input} ${classes.inputHalf}`}>
            <Box mt={2}>
              <Typography color="textSecondary">
                Quantas vezes será cobrado? (Ex: 12)
              </Typography>
            </Box>
            <RHFInput
              as={
                <TextField
                  label="Quantidade"
                  type="number"
                  margin="dense"
                  fullWidth
                  inputRef={inputQuantityRef}
                  className={classes.input}
                  error={!!errors.quantity}
                  helperText={errors?.quantity?.message}
                  inputProps={{ min: 0 }}
                  disabled={
                    !item.collapseSettings.edit ||
                    checkRecurrentValue('quantity', values.quantity)
                  }
                />
              }
              name="quantity"
              register={register}
              setValue={setValue}
              onChange={e => handleChangeQuantity(e, 'quantity')}
              mode="onChange"
            />
            <FormControlLabel
              control={<Checkbox size="small" />}
              label={'Recorrente (tempo indeterminado)'}
              size="small"
              onChange={e => handleChangeCheck(e, 'quantity', setValue)}
              checked={checkRecurrentValue('quantity', values.quantity)}
              disabled={!item.collapseSettings.edit}
            />
          </Box>

          <Box className={`${classes.input} ${classes.inputHalf}`}>
            <Box mt={2}>
              <Typography color="textSecondary">
                Valor de cada cobrança
              </Typography>
            </Box>
            <RHFInput
              as={
                <InputCurrency
                  label="Valor"
                  margin="dense"
                  fullWidth
                  className={classes.input}
                  error={!!errors.price}
                  helperText={errors?.price?.message}
                  disabled={!item.collapseSettings.edit}
                />
              }
              name="price"
              register={register}
              setValue={setValue}
              mode="onChange"
            />
          </Box>
        </div>

        <Typography variant="h6" className={classes.discountTitle}>
          Desconto
        </Typography>

        <div className={classes.rowInput}>
          <Box className={`${classes.input} ${classes.inputHalf}`}>
            <Box mt={2}>
              <Typography color="textSecondary">
                Quantas cobranças terão desconto? (Ex: 3)
              </Typography>
            </Box>
            <RHFInput
              as={
                <TextField
                  label="Quantidade"
                  type="number"
                  margin="dense"
                  fullWidth
                  className={classes.input}
                  error={!!errors['contractPlanEntries[0].quantity']}
                  helperText={
                    errors &&
                    errors['contractPlanEntries[0].quantity'] &&
                    errors['contractPlanEntries[0].quantity'].message
                  }
                  inputProps={{ min: 0 }}
                  disabled={
                    !item.collapseSettings.edit ||
                    checkRecurrentValue(
                      'contractPlanEntries[0].quantity',
                      values['contractPlanEntries[0].quantity'],
                    )
                  }
                />
              }
              name="contractPlanEntries[0].quantity"
              register={register}
              setValue={setValue}
              onChange={e =>
                handleChangeQuantity(e, 'contractPlanEntries[0].quantity')
              }
              mode="onChange"
            />
            <FormControlLabel
              control={<Checkbox size="small" />}
              label={'Recorrente (tempo indeterminado)'}
              size="small"
              checked={checkRecurrentValue(
                'contractPlanEntries[0].quantity',
                values['contractPlanEntries[0].quantity'],
              )}
              onChange={e =>
                handleChangeCheck(
                  e,
                  'contractPlanEntries[0].quantity',
                  setValue,
                )
              }
              disabled={!item.collapseSettings.edit}
            />
          </Box>

          <Box className={`${classes.input} ${classes.inputHalf}`}>
            <Box mt={2}>
              <Typography color="textSecondary">
                Informe o valor do desconto
              </Typography>
            </Box>
            <RHFInput
              as={
                <InputCurrency
                  label="Valor"
                  margin="dense"
                  fullWidth
                  className={classes.input}
                  error={!!errors['contractPlanEntries[0].price']}
                  helperText={
                    errors &&
                    errors['contractPlanEntries[0].price'] &&
                    errors['contractPlanEntries[0].price'].message
                  }
                  disabled={!item.collapseSettings.edit}
                />
              }
              name="contractPlanEntries[0].price"
              register={register}
              setValue={setValue}
              mode="onChange"
            />
          </Box>
        </div>
      </div>

      <Divider />

      <div className={classes.buttonGroup}>
        <div>
          {item.collapseSettings.edit ? (
            <>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                className={classes.buttonSave}
              >
                Salvar
              </Button>
              {item.collapseSettings.updatedOnce && (
                <Button
                  variant="outlined"
                  color="primary"
                  type="button"
                  className={classes.buttonSave}
                  onClick={handleClickCancelButton}
                >
                  Cancelar
                </Button>
              )}
            </>
          ) : (
            <Button
              variant="contained"
              color="primary"
              type="button"
              onClick={handleClickEditButton}
            >
              Editar
            </Button>
          )}
        </div>

        {collapse.data.length > 1 && (
          <Tooltip title="Remover">
            <IconButton aria-label="Remover" onClick={handleClickRemoveButton}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
      </div>
    </form>
  )
}

export default CollapseBody
